/**
 * One metric over time, per site (§7.2) — a line per site in the site's own
 * colour slot, a legend whenever there is more than one series, a tooltip
 * that lists every series at the hovered day, a hollow marker on any day
 * whose stored figure is an estimate (§7.4), the pre-launch period shaded
 * (§7.5), and a table view because two of the four slots sit under 3:1 on
 * the light surface (dataviz relief rule).
 *
 * Clean axes (operator 2026-09-22): the y-axis is pinned at zero; a spike
 * above robustCeiling() is drawn at the top edge with a ▲ marker instead of
 * flattening every other day (true value in the tooltip and the table); a
 * negative day (net unfollows) is drawn at zero. Days before the metric's
 * collection began are shaded "not collected" so an empty stretch never
 * reads as zero.
 */
import { useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';
import { Table2, LineChart as LineChartIcon } from 'lucide-react';
import type { MetricDefinition } from '../../lib/api/analytics.js';
import {
  type DailyShape, LAUNCH_ANCHOR, SITE_LABEL, addDays, compact, displayShape, estKey, rawKey, siteSlot,
} from '../../lib/analyticsShape.js';
import { EstimatedTag, VisibilityTag } from './StatTile.js';

const BASIS_LABEL: Record<string, string> = {
  daily: 'daily',
  rolling_30d: 'rolling 30 days',
  snapshot_24h: '24-hour snapshot',
  point_in_time: 'point in time',
  monthly: 'monthly',
};

const MIN_SHADED_GAP_DAYS = 7;
const DENSE_RANGE_DAYS = 120;

function seriesColour(site: string): string {
  return site === 'network' ? 'var(--viz-series-1)' : `var(--viz-series-${siteSlot(site)})`;
}

function seriesLabel(site: string): string {
  return site === 'network' ? 'Network (all sites)' : (SITE_LABEL[site] ?? site);
}

function shortDay(d: string): string {
  const dt = new Date(`${d}T00:00:00Z`);
  return dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function ChartTooltip({ active, payload, label }: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload as Record<string, unknown> | undefined;
  return (
    <div className="rounded-lg border border-grey-200 bg-surface px-3 py-2 shadow-md text-xs min-w-[160px]">
      <p className="font-medium text-grey-500 mb-1">{typeof label === 'string' ? shortDay(label) : label}</p>
      <ul className="space-y-0.5">
        {payload.map((entry) => {
          const key = String(entry.dataKey ?? '');
          const est = row?.[estKey(key)] === true;
          // The chart draws a clipped/clamped copy; the tooltip tells the truth.
          const raw = row?.[rawKey(key)];
          const v = typeof raw === 'number' ? raw : typeof entry.value === 'number' ? entry.value : null;
          return (
            <li key={key} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-grey-500">
                <span className="inline-block h-0.5 w-3 rounded" style={{ background: entry.color ?? seriesColour(key) }} aria-hidden />
                {seriesLabel(key)}
              </span>
              <span className="font-semibold text-grey-900 tabular-nums">
                {v == null ? 'no data' : v.toLocaleString()}{est ? ' ≈' : ''}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export interface SeriesChartProps {
  title: string;
  definition?: MetricDefinition;
  shape: DailyShape;
  /** Text under the title: what the figure is, from the catalog notes or the plan. */
  note?: string;
  source: string;
  /** Height of the plot area in px. */
  height?: number;
  /** Faded while a refetch is in flight — the frame is kept (dataviz interaction rule). */
  stale?: boolean;
  /** First day this metric was stored — earlier days are shaded "not collected". */
  collectedFrom?: string | null;
  /** First day with a non-zero value, when later than collectedFrom (zeros before a site or Page existed). */
  activityFrom?: string | null;
  /** A dated change in how the metric is MEASURED — drawn as a line so a step
   *  in the series is not read as a change in the world. */
  marker?: { date: string; label: string };
}

export function SeriesChart({ title, definition, shape, note, source, height = 220, stale, collectedFrom, activityFrom, marker }: SeriesChartProps) {
  const [table, setTable] = useState(false);
  const multi = shape.sites.length > 1;
  const showPrelaunch = shape.days.length > 0 && shape.days[0] < LAUNCH_ANCHOR;
  const view = displayShape(shape);
  const ceiling = view.ceiling;
  // Shade only when the range reaches back before collection AND past it
  // (the shaded span ends on the day before the first collected day), and
  // only for a gap worth seeing — a one-day sliver at the edge of a 12-month
  // range is noise, not information.
  const notCollectedTo = collectedFrom && shape.days.length > 0 && shape.days[shape.days.length - 1] >= collectedFrom
    && shape.days.indexOf(collectedFrom) >= MIN_SHADED_GAP_DAYS
    ? addDays(collectedFrom, -1)
    : null;
  // Estimate markers on every day of a long range are clutter; the count
  // stays in the footer and the table keeps the per-day flag.
  const showEstMarkers = shape.days.length <= DENSE_RANGE_DAYS;
  // Even quarters under a capped axis (recharts' own ticks go uneven when the
  // top is fixed).
  const yTicks = ceiling != null ? [0, 0.25, 0.5, 0.75, 1].map((f) => ceiling * f) : undefined;
  const showMarker = marker != null && shape.days.includes(marker.date);
  const anyData = shape.points.some((p) => shape.sites.some((s) => typeof p[s] === 'number'));

  return (
    <section className={`bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3 ${stale ? 'opacity-60 transition-opacity' : 'transition-opacity'}`} aria-busy={stale}>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-grey-900">{title}</h3>
          {note && <p className="text-xs text-grey-500 mt-0.5">{note}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {definition && <VisibilityTag visibility={definition.visibility} />}
          {definition && (
            <span className="rounded-full border border-grey-200 px-2 py-0.5 text-[10px] font-medium text-grey-500">
              {BASIS_LABEL[definition.basis] ?? definition.basis}
            </span>
          )}
          {definition && definition.indicator_class !== 'neutral' && (
            <span className="rounded-full border border-grey-200 px-2 py-0.5 text-[10px] font-medium text-grey-500">
              {definition.indicator_class}
            </span>
          )}
          <EstimatedTag count={shape.estimatedCount} />
          <button
            type="button"
            onClick={() => setTable((t) => !t)}
            className="inline-flex items-center gap-1 rounded-full border border-grey-200 px-2 py-0.5 text-[11px] font-medium text-grey-600 hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            aria-pressed={table}
          >
            {table ? <LineChartIcon className="h-3 w-3" /> : <Table2 className="h-3 w-3" />}
            {table ? 'Chart' : 'Table'}
          </button>
        </div>
      </header>

      {!anyData ? (
        <div className="flex items-center justify-center text-sm text-grey-400" style={{ height }}>
          No days collected in this range
        </div>
      ) : table ? (
        <div className="overflow-x-auto" style={{ maxHeight: height + 40 }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface">
              <tr className="text-left text-grey-500">
                <th className="py-1 pr-3 font-medium">Day</th>
                {shape.sites.map((s) => (
                  <th key={s} className="py-1 pr-3 font-medium text-right">{seriesLabel(s)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...shape.points].reverse().map((p) => (
                <tr key={p.date} className="border-t border-grey-100 text-grey-900">
                  <td className="py-1 pr-3 text-grey-500">{p.date}</td>
                  {shape.sites.map((s) => {
                    const v = p[s];
                    return (
                      <td key={s} className="py-1 pr-3 text-right tabular-nums">
                        {typeof v === 'number' ? v.toLocaleString() : <span className="text-grey-300">—</span>}
                        {p[estKey(s)] === true ? <span className="text-warning-600" title="estimated"> ≈</span> : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={view.points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="var(--viz-grid)" vertical={false} />
              {showPrelaunch && (
                <ReferenceArea x1={shape.days[0]} x2={LAUNCH_ANCHOR} fill="var(--viz-prelaunch)" fillOpacity={1} ifOverflow="hidden" />
              )}
              {notCollectedTo && (
                <ReferenceArea
                  x1={shape.days[0]}
                  x2={notCollectedTo}
                  fill="var(--grey-200)"
                  fillOpacity={0.55}
                  ifOverflow="hidden"
                  label={{ value: 'not collected', position: 'insideTop', fill: 'var(--grey-500)', fontSize: 10 }}
                />
              )}
              <XAxis
                dataKey="date"
                tickFormatter={shortDay}
                minTickGap={36}
                tick={{ fill: 'var(--grey-500)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--grey-200)' }}
                tickLine={false}
              />
              <YAxis
                width={44}
                tickFormatter={(v: number) => compact(v)}
                tick={{ fill: 'var(--grey-500)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                domain={[0, ceiling ?? 'auto']}
                ticks={yTicks}
                allowDataOverflow={ceiling != null}
              />
              {showMarker && marker && (
                <ReferenceLine
                  x={marker.date}
                  stroke="var(--grey-400)"
                  strokeDasharray="3 3"
                  ifOverflow="hidden"
                  label={{ value: marker.label, position: 'insideTopRight', fill: 'var(--grey-500)', fontSize: 10 }}
                />
              )}
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'var(--grey-300)', strokeWidth: 1 }} isAnimationActive={false} />
              {multi && (
                <Legend
                  verticalAlign="top"
                  height={24}
                  iconType="plainline"
                  itemSorter={null}
                  formatter={(value: string) => <span className="text-xs text-grey-600">{seriesLabel(value)}</span>}
                />
              )}
              {shape.sites.map((s) => (
                <Line
                  key={s}
                  type="monotone"
                  dataKey={s}
                  name={s}
                  stroke={seriesColour(s)}
                  strokeWidth={2}
                  connectNulls={false}
                  isAnimationActive={false}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--surface)' }}
                  dot={(props: { cx?: number; cy?: number; payload?: Record<string, unknown>; index?: number }) => {
                    const raw = props.payload?.[rawKey(s)];
                    if (ceiling != null && typeof raw === 'number' && raw > ceiling && props.cx != null && props.cy != null) {
                      // Above the axis: a ▲ at the top edge, the value on hover.
                      return (
                        <path
                          key={`${s}-${props.index}`}
                          d={`M ${props.cx} ${props.cy - 1} l 4 7 l -8 0 z`}
                          fill={seriesColour(s)}
                        >
                          <title>{`${seriesLabel(s)} ${raw.toLocaleString()} — above the axis`}</title>
                        </path>
                      );
                    }
                    const est = showEstMarkers && props.payload?.[estKey(s)] === true;
                    if (!est || props.cx == null || props.cy == null) return <g key={`${s}-${props.index}`} />;
                    return (
                      <circle
                        key={`${s}-${props.index}`}
                        cx={props.cx}
                        cy={props.cy}
                        r={3}
                        fill="var(--surface)"
                        stroke={seriesColour(s)}
                        strokeWidth={2}
                      >
                        <title>estimated</title>
                      </circle>
                    );
                  }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <footer className="text-[11px] text-grey-400 flex flex-wrap gap-x-3 gap-y-1">
        <span>{source}</span>
        {shape.through && <span>through {shape.through}</span>}
        {shape.estimatedCount > 0 && (showEstMarkers
          ? <span>≈ hollow marker = estimated day</span>
          : <span>≈ {shape.estimatedCount} estimated site-days (markers hidden at this range — Table shows them)</span>)}
        {showPrelaunch && <span>shaded = before the {LAUNCH_ANCHOR} launch anchor</span>}
        {collectedFrom && <span>collected since {collectedFrom}{activityFrom && activityFrom > collectedFrom ? ` · first activity ${activityFrom}` : ''}</span>}
        {notCollectedTo && <span>grey = not collected</span>}
        {showMarker && marker && <span>dashed line {marker.date} = {marker.label}; the step there is a measurement change, not traffic</span>}
        {view.clippedHigh > 0 && ceiling != null && (
          <span>▲ {view.clippedHigh} {view.clippedHigh === 1 ? 'day' : 'days'} above {compact(ceiling)}, drawn at the top edge — hover or Table for the value</span>
        )}
        {view.clampedNegative > 0 && <span>{view.clampedNegative} negative {view.clampedNegative === 1 ? 'day' : 'days'} drawn at zero</span>}
      </footer>
    </section>
  );
}
